import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { COLORS } from './constants';

export interface TipProps {
  text?: string;
  content?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  /** Force the tooltip placement; defaults to "auto" (above unless clipped). */
  placement?: 'auto' | 'top';
  fixedWidth?: number;
  tooltipContainerRef?: React.RefObject<HTMLElement | null>;
}

/** Touch-primary devices (mobile, devtools mobile mode, tablets). On these we
 *  use click-toggle instead of hover, because synthetic mouse events on a tap
 *  fire mouseenter+mouseleave together and the tooltip flashes shut. */
const isTouchPrimary = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

export const Tip = ({
  text,
  content,
  children,
  style: wrapStyle,
  placement = 'auto',
  fixedWidth,
  tooltipContainerRef,
}: TipProps) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0, yBottom: 0, flipped: false });
  const ref = useRef<HTMLSpanElement>(null);

  // Support both `text` and `content` prop names for flexibility
  const tipText = text ?? content;

  const positionFromRect = (rect: DOMRect) => {
    const centerX = rect.left + rect.width / 2;
    // Flip below only if tooltip (120px conservative height estimate) would clip the viewport top
    const flipped = placement === 'top' ? false : rect.top - 8 - 120 < 0;
    return { x: centerX, y: flipped ? rect.bottom : rect.top, yBottom: rect.bottom, flipped };
  };

  const handleEnter = (e: React.MouseEvent<HTMLSpanElement>) => {
    // On touch devices the synthetic mouseenter fires alongside mouseleave on
    // a tap and would cause the tooltip to flash. Click handles those cases.
    if (isTouchPrimary()) return;
    setPos(positionFromRect(e.currentTarget.getBoundingClientRect()));
    setShow(true);
  };

  const handleLeave = () => {
    if (isTouchPrimary()) return;
    setShow(false);
  };

  const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (!isTouchPrimary()) return;
    // Stop the click from immediately bubbling to the document-level
    // outside-click listener that we wire up while open.
    e.stopPropagation();
    if (show) {
      setShow(false);
    } else {
      setPos(positionFromRect(e.currentTarget.getBoundingClientRect()));
      setShow(true);
    }
  };

  // Touch-only: dismiss on outside tap. Skip the first tick so the click that
  // opened the tooltip doesn't immediately close it.
  useEffect(() => {
    if (!show || !isTouchPrimary()) return;
    const onOutside = (ev: MouseEvent | TouchEvent) => {
      const target = ev.target as Node | null;
      if (target && ref.current && !ref.current.contains(target)) {
        setShow(false);
      }
    };
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', onOutside);
      document.addEventListener('touchstart', onOutside);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
    };
  }, [show]);

  if (!tipText) {
    return <span style={wrapStyle}>{children}</span>;
  }

  // Clamp horizontally so the tooltip never bleeds past the viewport edges
  // and FORCE a fixed width on mobile so left/right tooltips render at the
  // exact same shape regardless of trigger position or text length.
  // (maxWidth alone lets the browser shrink-to-fit, which produced the
  // tall/narrow "right tooltip" artifact.)
  const EDGE_PAD = 12;
  const VIEWPORT_W = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const IS_MOBILE = VIEWPORT_W < 900;
  const TIP_WIDTH = IS_MOBILE
    ? Math.min(280, VIEWPORT_W - 2 * EDGE_PAD)
    : fixedWidth ?? 260;
  const HALF_W = TIP_WIDTH / 2;
  const container = tooltipContainerRef?.current;
  const containerRect = container?.getBoundingClientRect();
  const containerW = container?.offsetWidth;
  const clampedX = containerRect && containerW
    ? Math.max(
        HALF_W + EDGE_PAD,
        Math.min(pos.x - containerRect.left, containerW - HALF_W - EDGE_PAD),
      )
    : Math.max(
        HALF_W + EDGE_PAD,
        Math.min(pos.x, VIEWPORT_W - HALF_W - EDGE_PAD),
      );
  const top = containerRect
    ? pos.flipped
      ? pos.yBottom - containerRect.top + 8
      : pos.y - containerRect.top - 8
    : pos.flipped
      ? pos.y + 8
      : pos.y - 8;

  return (
    <span
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={handleClick}
      style={{
        cursor: 'help',
        borderBottom: '1px dotted var(--tx-line)',
        ...wrapStyle,
      }}>
      {children}
      {show &&
        createPortal(
          <span
            style={{
              position: container ? 'absolute' : 'fixed',
              left: clampedX,
              top,
              transform: pos.flipped ? 'translateX(-50%)' : 'translate(-50%, -100%)',
              background: 'rgba(var(--tx-ink-rgb), 0.05)',
              borderRadius: '12px',
              padding: '12px 16px',
              ...(IS_MOBILE || fixedWidth
                ? { width: TIP_WIDTH, boxSizing: 'border-box' as const }
                : { maxWidth: TIP_WIDTH }),
              fontSize: 12,
              color: COLORS.text,
              lineHeight: 1.55,
              zIndex: 9999,
              pointerEvents: 'none',
              fontWeight: 400,
              letterSpacing: 0,
              textTransform: 'none' as const,
              fontStyle: 'normal',
              textAlign: 'left' as const,
            }}>
            {tipText}
          </span>,
          container ?? document.body,
        )}
    </span>
  );
};

export default Tip;
